import React, { useEffect, useRef } from 'react';
import styled, { useTheme } from 'styled-components';

// Two soft light sources behind the page whose colour follows the section in view.
// Colours are OKLCH [lightness, chroma, hue]; low chroma and opacity keep it a tint, not a gradient show.
type Light = [number, number, number];

const palette: Record<string, [Light, Light]> = {
  home: [[0.7, 0.12, 40], [0.72, 0.08, 20]],
  about: [[0.7, 0.05, 250], [0.76, 0.05, 70]],
  craft: [[0.66, 0.09, 275], [0.7, 0.07, 230]],
  experience: [[0.7, 0.07, 185], [0.7, 0.05, 240]],
  skills: [[0.68, 0.07, 300], [0.72, 0.06, 50]],
  projects: [[0.7, 0.1, 40], [0.68, 0.06, 250]],
  contact: [[0.7, 0.07, 245], [0.72, 0.05, 190]],
};

// On dark backgrounds warm hues read as red, so dark mode swaps them for cool ones.
const darkOverrides: Record<string, [Light, Light]> = {
  // The iPhone's blue lock screen rather than the laptop's orange glow.
  home: [[0.68, 0.1, 262], [0.72, 0.07, 228]],
  about: [[0.7, 0.05, 250], [0.74, 0.05, 185]],
  skills: [[0.68, 0.07, 300], [0.72, 0.05, 190]],
  projects: [[0.7, 0.08, 262], [0.72, 0.06, 200]],
};

// Per theme: how strongly each light tints the page.
const strength = { light: 0.2, dark: 0.22 };

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background:
    radial-gradient(70vmax 50vmax at 8% -12%, var(--light-a, transparent), transparent 70%),
    radial-gradient(60vmax 45vmax at 100% 112%, var(--light-b, transparent), transparent 70%);
`;

const mix = (from: number, to: number, amount: number) => from + (to - from) * amount;

// Hue takes the short way round the colour wheel.
const mixHue = (from: number, to: number, amount: number) => {
  const delta = ((to - from + 540) % 360) - 180;
  return (from + delta * amount + 360) % 360;
};

const mixLight = (a: Light, b: Light, amount: number): Light => [mix(a[0], b[0], amount), mix(a[1], b[1], amount), mixHue(a[2], b[2], amount)];

const toCss = ([l, c, h]: Light, alpha: number) => `oklch(${(l * 100).toFixed(1)}% ${c.toFixed(3)} ${h.toFixed(1)} / ${alpha})`;

// Holds a section's colours until its last third, then eases into the next section's.
const handover = (t: number) => {
  const x = Math.min(1, Math.max(0, (t - 0.65) / 0.35));
  return x * x * (3 - 2 * x);
};

const AmbientBackground: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const dark = theme.name === 'dark';
  const alpha = dark ? strength.dark : strength.light;

  useEffect(() => {
    const element = ref.current;
    const main = document.querySelector('main');
    if (!element || !main) return;

    let sections: { key: string; top: number; height: number }[] = [];
    let frame = 0;

    const paint = () => {
      frame = 0;
      if (!sections.length) return;
      const focus = window.scrollY + window.innerHeight * 0.5;
      let index = sections.findIndex(section => focus < section.top + section.height);
      if (index === -1) index = sections.length - 1;
      const current = sections[index];
      const next = sections[Math.min(index + 1, sections.length - 1)];
      const amount = handover((focus - current.top) / current.height);
      const colours = (key: string) => (dark && darkOverrides[key]) || palette[key] || palette.about;
      const [a1, b1] = colours(current.key);
      const [a2, b2] = colours(next.key);
      element.style.setProperty('--light-a', toCss(mixLight(a1, a2, amount), alpha));
      element.style.setProperty('--light-b', toCss(mixLight(b1, b2, amount), alpha));
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(paint);
    };

    const measure = () => {
      sections = Array.from(main.querySelectorAll<HTMLElement>(':scope > section')).map(section => ({
        key: section.id,
        top: section.getBoundingClientRect().top + window.scrollY,
        height: section.offsetHeight,
      }));
      schedule();
    };

    measure();
    // Section heights shift as fonts, images and App Store data load.
    const observer = new ResizeObserver(measure);
    observer.observe(main);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', measure);
    };
  }, [alpha, dark]);

  return <Backdrop ref={ref} aria-hidden="true" />;
};

export default AmbientBackground;
